import { Mail } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { LinkedInMark, WhatsAppMark } from "@/components/crm/layout/BrandMarks";
import {
  DomainSwitcher,
  firstLiveKey,
  type DomainOption,
} from "@/components/crm/layout/DomainSwitcher";

/**
 * THE MESSAGE-CHANNEL SWITCHER — on EVERY channel surface, not just one of them (PO 2026-09-21:
 * *"THIS SHOULD BE VISIBLE IN all 3 tabs, whether i am in whatsapp, email, linkedin.. right ? i shall
 * be able to switch back and forth seemingless !"*).
 *
 * He is right, and the first version was a one-way door: the switcher lived only on `/messages`, so
 * from the LinkedIn surface there was no way back at all — a "switcher" that can only be used from one
 * of the places it switches between.
 *
 * THE SELECTION IS DERIVED FROM THE ROUTE, never from local state, so what the switcher highlights can
 * never disagree with where the person actually is. That was the other half of the same defect: a
 * local `useState(() => firstLiveKey(...))` would show "Email" while the user stood on LinkedIn.
 *
 * ONE DEFINITION, MANY HOSTS: the channel list, the brand marks and the colours live here, and every
 * surface renders this component. A second list would drift the moment a channel is added (the rule
 * D-133-023 already enforces for the queue surface).
 *
 * ALL THREE WERE LIVE as of 2026-09-21 — WhatsApp spent a day as the honest "coming soon" chip and
 * became a real button the moment `WhatsAppMessagesPage.tsx` existed to receive it. That order
 * mattered, and it is the order this file follows again: the chip is a promise to build something,
 * and a promise is kept by BUILDING it, not by leaving a broken board behind the label.
 *
 * ⚠️ R2-24 (2026-10-03) — LINKEDIN AND WHATSAPP ARE BACK TO "coming soon". Not because the boards
 * were removed, but because their engines are not running beside raisy, so each visit opened a
 * request that failed, painted an error, and reloaded the app: *"first i see an 'ERROR', then i see
 * a FULL RELOAD.. and then i see 'The LinkedIn engine is not answering'… TERRIBLE EXPERIENCE. just
 * lock it up for now… this must load in milliseconds."* The chip and the route say the same thing
 * now, and neither reaches an engine.
 */

export const MESSAGE_CHANNELS: DomainOption[] = [
  { key: "email", label: "Email", icon: Mail, live: true },
  // The brand marks live in `BrandMarks` because lucide carries none; the colours sit beside the
  // labels they belong to, so the switcher itself stays brand-agnostic.
  //
  // ⚠️ R2-24 — LINKEDIN AND WHATSAPP ARE LOCKED, NOT LIVE (PO 2026-10-03): *"for the linkedin,
  // first i see an 'ERROR', then i see a FULL RELOAD.. and then i see 'The LinkedIn engine is not
  // answering'… TERRIBLE EXPERIENCE. just lock it up for now, same also for whatsapp. coming soon
  // or something. BUT YEAH THIS CANT HAPPEN."* A channel whose engine is not running is not a
  // channel with a slow start — it is a promise that has not been kept yet, and it must say so
  // BEFORE anyone waits for it. `live: false` renders the dashed "coming soon" statement
  // `DomainSwitcher` already uses for the capital surface's Procurement and Recruitment, and the
  // routes are locked to the same sentence in `ChannelComingSoon`, so neither the chip nor a
  // bookmarked URL can reach an engine that is not there.
  { key: "linkedin", label: "LinkedIn", icon: LinkedInMark, live: false, tone: "text-[#0A66C2]" },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsAppMark, live: false, tone: "text-[#25D366]" },
];

/** Which route each LIVE channel lives on — the switcher navigates, it does not swap in place. */
export const MESSAGE_CHANNEL_ROUTES: Record<string, string> = {
  email: "/messages",
  linkedin: "/linkedin-messages",
  whatsapp: "/whatsapp-messages",
};

export const MessagesChannelSwitcher = ({ className }: { className?: string }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const current =
    Object.entries(MESSAGE_CHANNEL_ROUTES).find(([, route]) => pathname.startsWith(route))?.[0] ??
    firstLiveKey(MESSAGE_CHANNELS);

  return (
    <div className={className}>
      <DomainSwitcher
        ariaLabel="Message channel"
        options={MESSAGE_CHANNELS}
        value={current}
        onChange={(key) => {
          const route = MESSAGE_CHANNEL_ROUTES[key];
          if (route) navigate(route);
        }}
      />
    </div>
  );
};
